"use client";

import { LeftDouble, LeftIcon, RightDouble, RightIcon } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

import { IconButton } from "./icon-button";

interface PaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItemsCount: number;
  itemsCountPerPage: number;
  maxPageButtons?: number;
}

const Pagination = ({
  currentPage,
  onPageChange,
  totalItemsCount,
  itemsCountPerPage,
  maxPageButtons = 10,
}: PaginationProps) => {
  const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);

  if (totalPages <= 0) return null;

  const pageRange = maxPageButtons;
  const currentGroup = Math.floor((currentPage - 1) / pageRange);
  const startPage = currentGroup * pageRange + 1;
  const endPage = Math.min(startPage + pageRange - 1, totalPages);

  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const isFirstGroup = currentGroup === 0;
  const isLastGroup = endPage >= totalPages;

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-1">
        <IconButton
          aria-label="Previous group"
          size="small"
          className="text-gray-800"
          icon={<LeftDouble />}
          disabled={isFirstGroup}
          onClick={() => onPageChange(Math.max(1, startPage - pageRange))}
        />
        <IconButton
          aria-label="Previous"
          size="small"
          className="text-gray-800"
          icon={<LeftIcon />}
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        />
      </div>
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          aria-selected={page === currentPage || undefined}
          onClick={() => onPageChange(page)}
          className={cn(
            "inline-flex items-center justify-center",
            "w-8 h-8 rounded-4",
            "text-sm font-medium leading-[1.43] tracking-[-0.005em]",
            "cursor-pointer transition-colors duration-200",
            "focus-visible:outline-none focus-visible:ring-0",
            page === currentPage
              ? "bg-gray-400"
              : "bg-transparent hover:bg-gray-200",
          )}
        >
          {page}
        </button>
      ))}
      <div className="flex items-center">
        <IconButton
          aria-label="Next"
          size="small"
          className="text-gray-800"
          icon={<RightIcon />}
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
        <IconButton
          aria-label="Next group"
          size="small"
          className="text-gray-800"
          icon={<RightDouble />}
          disabled={isLastGroup}
          onClick={() => onPageChange(Math.min(totalPages, startPage + pageRange))}
        />
      </div>
    </div>
  );
};

export { Pagination, type PaginationProps };
